import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { PullQuote, ImageFigure, AccentMark } from "@/components/admin/tiptap-extensions";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { html as htmlLang } from "@codemirror/lang-html";
import { html_beautify } from "js-beautify";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Heading2,
  Heading3,
  Quote,
  List as BulletIcon,
  ListOrdered,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Minus,
  Image as ImageInsertIcon,
  Code as CodeIcon,
  Code2 as CodeBlockIcon,
  Megaphone as PullQuoteIcon,
  FileCode2 as HtmlModeIcon,
  Sparkles as BeautifyIcon,
} from "lucide-react";

/**
 * Normalise HTML pasted from Google Docs / Word / ChatGPT so the editor
 * doesn't inherit alien fonts, colors, spacing, or malformed nested markup.
 * Preserves: paragraphs, headings, bold/italic, lists, links.
 * Drops: style/class/font/color/span/o:p noise plus MS Word conditional tags.
 */
function normalisePastedHtml(html) {
  if (!html) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (!root) return html;
    // Kill Word/Docs junk wrappers
    root.querySelectorAll("style,meta,link,o\\:p,xml,script").forEach((n) => n.remove());
    // Strip every style/class/lang attribute across the whole subtree, plus
    // legacy presentation attrs. Keeps `href` on <a> and `src`/`alt` on <img>.
    const KEEP = { A: ["href", "title"], IMG: ["src", "alt", "title"] };
    root.querySelectorAll("*").forEach((el) => {
      const allowed = KEEP[el.tagName] || [];
      [...el.attributes].forEach((a) => {
        if (!allowed.includes(a.name)) el.removeAttribute(a.name);
      });
    });
    // Unwrap non-semantic spans/divs/fonts that survived attribute stripping —
    // TipTap's schema will otherwise wrap them into paragraphs weirdly.
    root.querySelectorAll("span,font").forEach((el) => {
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });
    return root.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Split a top-level paragraph on double-`<br>` boundaries so ProseMirror sees
 * TWO textblocks instead of ONE containing a `hardBreak` fence. Fixes the
 * production bug where selecting the second visual paragraph + clicking H3
 * also converted the first because both lived in the same paragraph node.
 *
 * Rules:
 *   • 2+ consecutive `<br>` (with optional whitespace/text nodes between) →
 *     paragraph boundary. Content on each side becomes its own `<p>`.
 *   • Single `<br>` → preserved as hardBreak (legitimate line breaks like
 *     poetry, addresses, line-per-line callouts).
 *   • Empty resulting `<p>` fragments are discarded.
 *   • Non-`<p>` blocks (headings, lists, blockquotes, figures, hr, images)
 *     pass through untouched — the schema already forces them to be their
 *     own block.
 *
 * Idempotent + non-destructive: running twice yields the same HTML, and
 * paragraphs that never had `<br><br>` are returned byte-identical.
 */
function splitParagraphsOnDoubleBreaks(html) {
  if (!html || typeof window === "undefined") return html;
  // Cheap guard: if the string has no `<br><br>` pattern at all, short-circuit.
  if (!/<br\s*\/?>\s*<br\s*\/?>/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (!root) return html;
    // Walk a snapshot of children — we mutate the tree as we go.
    const kids = Array.from(root.children);
    for (const el of kids) {
      if (el.tagName !== "P") continue;
      // Detect ≥2 consecutive <br>. Collapse whitespace-only text nodes
      // between <br>s so `<br> \n <br>` counts as a double break too.
      const inner = el.innerHTML;
      if (!/<br\s*\/?>\s*<br\s*\/?>/i.test(inner)) continue;
      // Split on runs of ≥2 <br> (with any whitespace between). Single <br>
      // stays intact inside the resulting fragments.
      const pieces = inner.split(/(?:\s*<br\s*\/?>\s*){2,}/i);
      const nonEmpty = pieces.map((p) => p.trim()).filter((p) => p && p !== "&nbsp;");
      if (nonEmpty.length < 2) continue; // nothing to split
      const frag = doc.createDocumentFragment();
      for (const piece of nonEmpty) {
        const np = doc.createElement("p");
        np.innerHTML = piece;
        frag.appendChild(np);
      }
      el.parentNode.replaceChild(frag, el);
    }
    return root.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Editor-load normalization: fixes legacy article HTML that has multiple
 * visual paragraphs collapsed into one `<p>` via `<br><br>`. Applied at load
 * (setContent) and at paste. Non-destructive to storage — first user save
 * naturally rewrites the article with the corrected structure.
 */
function normaliseIncomingHtml(html) {
  return splitParagraphsOnDoubleBreaks(html);
}


export function BlogEditor({ value, onChange, placeholder }) {
  const fileInputRef = useRef(null);
  // "visual" (TipTap rich text) or "html" (CodeMirror source code)
  const [mode, setMode] = useState("visual");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  // Remember the HTML we most recently emitted. Guards the external-value
  // sync effect against parent autosave echoing back what we just typed and
  // triggering a `setContent` call that would blow away the caret / selection.
  const lastEmittedRef = useRef(value || "");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: false,
      }),
      Placeholder.configure({
        placeholder: placeholder || "Tulis isi artikel di sini…",
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-[#D2673E] underline underline-offset-2",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "rounded-xl border border-[#EAE7E0] w-full max-h-[70vh] object-cover my-4",
        },
      }),
      PullQuote,
      ImageFigure,
      AccentMark,
    ],
    // Normalise on initial load so legacy articles with <br><br> paragraph
    // fences arrive at ProseMirror as separate textblocks. Without this,
    // clicking H3 while inside "paragraph B" also converts "paragraph A"
    // because both live inside the same paragraph node.
    content: normaliseIncomingHtml(value || ""),
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const normalised = html === "<p></p>" ? "" : html;
      lastEmittedRef.current = normalised;
      onChange(normalised);
    },
    editorProps: {
      attributes: {
        "data-testid": "blog-editor-content",
        class:
          "prose prose-sm max-w-none min-h-[280px] px-4 py-3 focus:outline-none " +
          "prose-headings:font-serif-display prose-headings:text-[#1B2B4D] " +
          "prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-2 " +
          "prose-h3:text-xl prose-h3:mt-5 prose-h3:mb-2 " +
          "prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-[#1F2937] " +
          "prose-blockquote:border-l-4 prose-blockquote:border-[#D2673E] " +
          "prose-blockquote:bg-[#FDF6F0] prose-blockquote:italic prose-blockquote:text-[#4B5563] " +
          "prose-blockquote:rounded-r-md prose-a:text-[#D2673E]",
      },
      // Sanitize pasted HTML AND split paragraph-fences the same way as
      // load-time normalization. Pasting from Word/Docs/ChatGPT very often
      // ships `<br><br>` in place of `</p><p>` — this catches it before
      // TipTap builds a defective node.
      transformPastedHTML: (html) =>
        splitParagraphsOnDoubleBreaks(normalisePastedHtml(html)),
    },
  });

  // Only apply an EXTERNAL `value` change (draft restore, edit-post load).
  // If the incoming value is the same string we just emitted from onUpdate,
  // skip — otherwise `setContent` would rebuild the document mid-typing and
  // move the caret.
  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next === lastEmittedRef.current) return;
    const current = editor.getHTML();
    if (next === current || (!next && current === "<p></p>")) return;
    lastEmittedRef.current = next;
    // Normalise external HTML so legacy articles arriving via edit-post also
    // get their <br><br> paragraph fences split before ProseMirror sees them.
    editor.commands.setContent(normaliseIncomingHtml(next), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  // Every toolbar button MUST swallow `mousedown` to prevent the button from
  // stealing focus from the editor mid-click. Without this, clicking Bold on
  // a text selection can collapse the DOM selection before `toggleBold`
  // reaches ProseMirror — causing formatting to bleed onto neighbouring text
  // or the wrong block. This is the standard TipTap toolbar contract.
  const stopMouseDown = (e) => e.preventDefault();

  const btn = (active) =>
    "inline-flex items-center justify-center w-8 h-8 rounded-md border text-sm transition-colors " +
    (active
      ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
      : "bg-white border-[#EAE7E0] text-[#1F2937] hover:bg-[#F3F1ED]");

  const promptLink = () => {
    const previousUrl = editor.getAttributes("link").href || "";
    // eslint-disable-next-line no-alert
    const url = window.prompt("URL tautan (kosongkan untuk menghapus)", previousUrl);
    if (url === null) return; // cancel
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Format gambar tidak didukung (PNG/JPG/WEBP)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Ukuran gambar melebihi 8 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingCount((n) => n + 1);
    try {
      const res = await api.post("/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (!url) throw new Error("Upload gagal");
      // eslint-disable-next-line no-alert
      const caption = window.prompt("Keterangan gambar (opsional, boleh kosong)") || "";
      editor.chain().focus().setImageFigure({ src: url, alt: caption, caption }).run();
      toast.success("Gambar diunggah & disisipkan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah gambar");
    } finally {
      setUploadingCount((n) => Math.max(0, n - 1));
    }
  };

  // Drag & drop / paste helpers — upload every image the admin drops into the
  // editor. Non-image drops fall through to TipTap's default behaviour so link
  // / text drops keep working.
  const handleDrop = async (e) => {
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await handleImageFile(f);
    }
  };
  const handlePaste = async (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    e.preventDefault();
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await handleImageFile(f);
    }
  };

  return (
    <div
      className="rounded-lg border border-[#EAE7E0] bg-white"
      data-testid="blog-editor"
    >
      {/* Toolbar — in HTML mode the rich-text buttons are hidden to make it
          obvious the user is in a different editing mode, and a large
          "Kembali ke Visual" button is offered so nobody gets stranded. */}
      {mode === "html" ? (
        <div
          className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-[#EAE7E0] bg-[#1B2B4D] text-white rounded-t-lg shadow-sm"
          data-testid="blog-editor-toolbar"
        >
          <div className="flex items-center gap-2">
            <HtmlModeIcon size={14} className="text-[#F3F1ED]" />
            <span className="text-[11px] uppercase tracking-widest font-semibold">
              Mode HTML aktif
            </span>
          </div>
          <button
            type="button"
            onMouseDown={stopMouseDown}
            onClick={() => {
              // Coming back from HTML mode → renormalise so any `<br><br>`
              // the editor typed by hand also gets promoted to separate <p>.
              const nextHtml = normaliseIncomingHtml(value || "");
              editor.commands.setContent(nextHtml, false);
              setMode("visual");
            }}
            data-testid="editor-btn-back-to-visual"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#D2673E] hover:bg-[#BE5730] text-white text-[12px] font-semibold transition-colors"
            title="Kembali ke mode Rich Text visual"
          >
            ← Kembali ke Visual
          </button>
        </div>
      ) : (
      <div
        className="sticky top-0 z-20 flex flex-nowrap md:flex-wrap items-center gap-1.5 px-2 py-1.5 border-b border-[#EAE7E0] bg-[#FBFAF6] rounded-t-lg shadow-[0_2px_6px_-4px_rgba(31,41,55,0.15)] overflow-x-auto md:overflow-x-visible"
        data-testid="blog-editor-toolbar"
      >
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={btn(editor.isActive("bold"))}
          data-testid="editor-btn-bold"
          title="Tebal (Ctrl+B)"
        >
          <BoldIcon size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={btn(editor.isActive("italic"))}
          data-testid="editor-btn-italic"
          title="Miring (Ctrl+I)"
        >
          <ItalicIcon size={15} />
        </button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" aria-hidden="true" />
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={btn(editor.isActive("heading", { level: 2 }))}
          data-testid="editor-btn-h2"
          title="Judul H2"
        >
          <Heading2 size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={btn(editor.isActive("heading", { level: 3 }))}
          data-testid="editor-btn-h3"
          title="Sub-judul H3"
        >
          <Heading3 size={15} />
        </button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" aria-hidden="true" />
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={btn(editor.isActive("bulletList"))}
          data-testid="editor-btn-bullet"
          title="Daftar berpoin"
        >
          <BulletIcon size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={btn(editor.isActive("orderedList"))}
          data-testid="editor-btn-ordered"
          title="Daftar bernomor"
        >
          <ListOrdered size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={btn(editor.isActive("blockquote"))}
          data-testid="editor-btn-quote"
          title="Kutipan"
        >
          <Quote size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().togglePullQuote().run()}
          className={btn(editor.isActive("pullQuote"))}
          data-testid="editor-btn-pullquote"
          title="Pull-quote (kutipan besar bergaya)"
        >
          <PullQuoteIcon size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleAccent().run()}
          className={btn(editor.isActive("accent"))}
          data-testid="editor-btn-accent"
          title="Warnai aksen (terracotta) — untuk hook phrase di judul atau kalimat"
        >
          <span
            className="font-serif inline-block leading-none"
            style={{ color: editor.isActive("accent") ? "#fff" : "#c1521f", fontWeight: 600 }}
          >
            Aa
          </span>
        </button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" aria-hidden="true" />
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={promptLink}
          className={btn(editor.isActive("link"))}
          data-testid="editor-btn-link"
          title="Sisipkan tautan"
        >
          <LinkIcon size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => fileInputRef.current?.click()}
          className={btn(false) + " gap-1 w-auto px-2 text-[11px] font-semibold"}
          data-testid="editor-btn-image-upload"
          title="Unggah gambar dari perangkat atau seret & lepas langsung ke editor"
        >
          <ImageInsertIcon size={14} />
          <span className="hidden sm:inline">Unggah</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          data-testid="editor-image-file-input"
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
              // eslint-disable-next-line no-await-in-loop
              await handleImageFile(f);
            }
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={btn(editor.isActive("code"))}
          data-testid="editor-btn-code"
          title="Kode inline"
        >
          <CodeIcon size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={btn(editor.isActive("codeBlock"))}
          data-testid="editor-btn-codeblock"
          title="Blok kode"
        >
          <CodeBlockIcon size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className={btn(false)}
          data-testid="editor-btn-hr"
          title="Garis pemisah"
        >
          <Minus size={15} />
        </button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" aria-hidden="true" />
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className={btn(false) + " disabled:opacity-40"}
          data-testid="editor-btn-undo"
          title="Undo (Ctrl+Z)"
        >
          <Undo2 size={15} />
        </button>
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className={btn(false) + " disabled:opacity-40"}
          data-testid="editor-btn-redo"
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 size={15} />
        </button>
        <span className="w-px h-5 bg-[#EAE7E0] mx-1" aria-hidden="true" />
        <button
          type="button"
          onMouseDown={stopMouseDown}
          onClick={() => setMode("html")}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#EAE7E0] bg-white hover:border-[#1B2B4D] hover:bg-[#F3F1ED] text-[11px] font-semibold uppercase tracking-wider text-[#1B2B4D] transition-colors"
          data-testid="editor-btn-html-mode"
          title="Edit kode HTML sumber"
        >
          <HtmlModeIcon size={13} /> HTML
        </button>
      </div>
      )}

      {/* Editor pane — switches between TipTap visual editor and CodeMirror HTML source. */}
      {mode === "visual" ? (
        <div
          className={`relative transition-colors ${isDragging ? "bg-[#FDF6F0] ring-2 ring-inset ring-[#D2673E]" : ""}`}
          onDragEnter={(e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setIsDragging(true); } }}
          onDragOver={(e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setIsDragging(true); } }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setIsDragging(false); }}
          onDrop={handleDrop}
          onPaste={handlePaste}
          data-testid="blog-editor-drop-zone"
        >
          <EditorContent editor={editor} />
          {isDragging && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#FDF6F0]/85 border-2 border-dashed border-[#D2673E] rounded-md">
              <div className="text-center">
                <ImageInsertIcon size={28} className="text-[#D2673E] mx-auto mb-1" />
                <div className="text-sm font-semibold text-[#8A3A1C]">Lepas gambar untuk mengunggah</div>
                <div className="text-[11px] text-[#4B5563]">PNG · JPG · WEBP · maks 8 MB</div>
              </div>
            </div>
          )}
          {uploadingCount > 0 && (
            <div className="absolute top-2 right-2 inline-flex items-center gap-2 rounded-full bg-[#1B2B4D] text-white text-[11px] px-3 py-1 shadow-lg" data-testid="blog-editor-uploading">
              <span className="inline-block w-2 h-2 rounded-full bg-[#F1C97A] animate-pulse" aria-hidden="true" />
              Mengunggah {uploadingCount} gambar…
            </div>
          )}
        </div>
      ) : (
        <div data-testid="blog-editor-html-pane" className="min-h-[400px]">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#EAE7E0] bg-[#FBFAF6]">
            <span className="text-[10px] uppercase tracking-widest text-[#8A8F99] font-semibold">
              Kode HTML sumber
            </span>
            <button
              type="button"
              data-testid="editor-btn-beautify"
              onClick={() => {
                const pretty = html_beautify(value || "", {
                  indent_size: 2,
                  wrap_line_length: 100,
                  preserve_newlines: true,
                  end_with_newline: true,
                  unformatted: ["code", "pre"],
                });
                onChange(pretty);
                toast.success("HTML dirapikan");
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#EAE7E0] bg-white hover:border-[#D2673E] hover:text-[#D2673E] text-[11px] font-medium text-[#1F2937] transition-colors"
              title="Rapikan HTML (Beautify)"
            >
              <BeautifyIcon size={12} /> Format HTML
            </button>
          </div>
          <div className="min-h-[400px] max-h-[70vh] overflow-y-auto">
            <CodeMirror
              value={value || ""}
              onChange={(next) => onChange(next)}
              extensions={[htmlLang(), EditorView.lineWrapping]}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLine: true,
                foldGutter: true,
                autocompletion: false,
              }}
              theme="light"
              minHeight="400px"
              className="text-[13px]"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogEditor;
